export type PreviewKind = 'video' | 'audio' | 'pdf' | 'image';

/**
 * Which preview a browser can render for a converted output, or null for none.
 *
 * Single source of truth, deliberately: the result card previously asked this question
 * twice — a "Play Media" button gated on video/av/audio and a "View Media" button gated on
 * a separate hardcoded still-image list — and the two disagreed, so some outputs offered
 * both and others offered nothing.
 *
 * This is the cheap synchronous question — "is this plausibly previewable?". The honest
 * per-browser answer lives in utils/media-capability.ts and is checked before the preview
 * actually opens, which is what lets heic/tiff/bmp appear here at all: an undecodable format
 * now degrades to a clear message instead of a broken image. Showing a broken image is worse than showing no preview;
 * supporting them needs runtime capability detection plus a server-rendered poster
 * fallback — T2.2/T2.3 in docs/proposed/RETENTION_FIXES.md.
 */
export function previewKindFor(toType?: string | null): PreviewKind | null {
    switch ((toType || '').toLowerCase()) {
        // Generic media buckets, plus the concrete extensions pages actually emit.
        // `mp4` alone is the second most common output on the site (15.2k/week in prod)
        // and used to get no preview at all, because the old gate only matched the
        // generic 'video'. We choose the output codec (H.264 by default), so these play.
        case 'video':
        case 'av':
        case 'mp4':
        case 'm4v':
        case 'webm':
        case 'ogv':
            return 'video';
        // Audio is its own kind: rendering it through <video> trips the zero-dimension
        // "undecodable" heuristic (audio is always 0x0), which condemned every playable
        // MP3 to the poster fallback -- and audio outputs have no server poster, so the
        // user got "Preview unavailable" for a file Chrome plays natively.
        case 'audio':
        case 'mp3':
        case 'wav':
        case 'ogg':
        case 'm4a':
        case 'aac':
        case 'flac':
            return 'audio';
        case 'pdf':
            return 'pdf';
        case 'gif':
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'webp':
        case 'avif':
        // heic/heif/tiff/bmp/`image` used to return null, so /compress-heic and
        // /compress-tiff — two of the worst pages for people downloading nothing — offered
        // no preview at all. They are listed now because utils/media-capability.ts probes
        // the browser before opening and falls back with an explanation, so a format this
        // browser cannot decode no longer renders as a broken image.
        case 'heic':
        case 'heif':
        case 'tiff':
        case 'tif':
        case 'bmp':
        case 'image':
        // SVG is safe here because the preview renders through <img src>, which does not
        // execute scripts or fetch subresources. It must never be inlined via innerHTML,
        // and never loaded from a blob: URL — blob: inherits our origin, which is strictly
        // worse than the remote URL. See T2.5.
        case 'svg':
            return 'image';
        default:
            return null;
    }
}
